/** @graphql agg **/
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import gql from 'graphql-tag';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { css } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import AvatarGroup from '@atlaskit/avatar-group';
import { Stack } from '@atlaskit/primitives/compiled';

import { useSessionData } from '@confluence/session-data';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import type { FlagDescriptor } from '@confluence/flags';
import { useCopyContentLink, PAGE_MODE } from '@confluence/share/entry-points/useCopyContentLink';

import type { ContextualInviteNudgeFlagBaseProps } from './ContextualInviteNudgeFlag';
import { PartyIcon } from './assets/PartyIcon';
import type {
	FirstPublishNthUserShareFlagRecommendationsQuery,
	FirstPublishNthUserShareFlagRecommendationsQueryVariables,
} from './__types__/FirstPublishNthUserShareFlagRecommendationsQuery';

const messages = defineMessages({
	// First share nudge on publish - nth user
	firstShareNudgeTitle: {
		id: 'contextual-invite-nudge-flag.first-share-nudge.title',
		defaultMessage: 'Don’t let your work go unseen',
		description:
			'This message appears as a title on a flag after a user publishes a page for the first time.',
	},
	firstShareNudgeDescription: {
		id: 'contextual-invite-nudge-flag.first-share-nudge.description',
		defaultMessage: 'Share this page to ensure your team can see and build on your contributions.',
		description:
			'This message appears as a description on a flag after a user publishes a page for the first time.',
	},
	firstShareNudgeCopyCTA: {
		id: 'contextual-invite-nudge-flag.first-share-nudge.copy',
		defaultMessage: 'Copy page link',
		description:
			'This message appears as button text on a flag after a user publishes a page for the first time.',
	},
	firstShareNudgeCopiedCTA: {
		id: 'contextual-invite-nudge-flag.first-share-nudge.copied',
		defaultMessage: 'Copied',
		description:
			'This message appears after a button was clicked to indicate that the page link was copied.',
	},
});

const FLAG_ID_BASE = 'endUserContextualInviteNudgeFlagShareVariant';
const END_USER_FLAG_ID_SHARE_VARIANT = 'contextual-invite-nudge-end-user-flag-share-variant';
const TIMEOUT_INTERVAL = 2000;
const copiedLinkStyles = css({
	color: token('color.text.disabled'),
});

const SmartsRecommendedUserQuery = gql`
	query FirstPublishNthUserShareFlagRecommendationsQuery($userId: String!, $tenantId: String!) {
		smarts {
			recommendedUser(
				recommendationsQuery: {
					context: { userId: $userId, tenantId: $tenantId, product: "confluence" }
					modelRequestParams: {
						caller: "first-publish-nth-user-share-flag"
						experience: "CgUserNearbyUser"
					}
					requestingUserId: $userId
					maxNumberOfResults: 3
				}
			) {
				id
				user {
					accountId
					name
					picture
				}
			}
		}
	}
`;

export const FirstPublishNthUserShareFlag = ({
	flags,
	source,
	contentType,
	contentId,
	spaceKey,
}: ContextualInviteNudgeFlagBaseProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isInviteLinkCopied, setIsInviteLinkCopied] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>();
	const { userId, cloudId } = useSessionData();
	const { copyContentLink } = useCopyContentLink({
		pageMode: PAGE_MODE.VIEW,
		contentType,
		contentId,
		spaceKey: spaceKey || '',
		source: FLAG_ID_BASE,
	});

	const { data, loading, error } = useQuery<
		FirstPublishNthUserShareFlagRecommendationsQuery,
		FirstPublishNthUserShareFlagRecommendationsQueryVariables
	>(SmartsRecommendedUserQuery, {
		variables: {
			userId: userId || '',
			tenantId: cloudId,
		},
		client: getAGGClient(),
		skip: !userId,
	});

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: FLAG_ID_BASE,
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	useEffect(() => {
		if (isInviteLinkCopied) {
			timer.current = setTimeout(() => {
				setIsInviteLinkCopied(false);
			}, TIMEOUT_INTERVAL);
		} else {
			clearInterval(timer.current);
		}

		return () => clearInterval(timer.current);
	}, [isInviteLinkCopied, setIsInviteLinkCopied, timer]);

	const onCopy = useCallback(() => {
		void copyContentLink();
		setIsInviteLinkCopied(true);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${FLAG_ID_BASE}CopyClick`,
				source,
			},
		}).fire();
	}, [copyContentLink, createAnalyticsEvent, source]);

	useEffect(() => {
		if (!loading) {
			const avatarGroupData =
				data?.smarts?.recommendedUser?.reduce((users: any[], currentUser) => {
					const name = currentUser?.user?.name;
					const picture = currentUser?.user?.name;

					if (name) {
						return [
							...users,
							{
								name,
								src: picture || '',
							},
						];
					}

					return users;
				}, []) || [];

			const sharePageDescriptor: FlagDescriptor = {
				id: END_USER_FLAG_ID_SHARE_VARIANT,
				messageId: END_USER_FLAG_ID_SHARE_VARIANT,
				messageType: 'engagement',
				title: <FormattedMessage {...messages.firstShareNudgeTitle} />,
				description: avatarGroupData.length ? (
					<Stack space="space.100">
						<FormattedMessage {...messages.firstShareNudgeDescription} />
						<AvatarGroup size="small" data={avatarGroupData} />
					</Stack>
				) : (
					<FormattedMessage {...messages.firstShareNudgeDescription} />
				),
				customIcon: <PartyIcon />,
				type: 'custom',
				actions: [
					isInviteLinkCopied
						? {
								content: (
									<div css={copiedLinkStyles}>
										<FormattedMessage {...messages.firstShareNudgeCopiedCTA} />
									</div>
								),
							}
						: {
								content: <FormattedMessage {...messages.firstShareNudgeCopyCTA} />,
								onClick: onCopy,
							},
				],
				close: 'never',
				onClose: () => {
					void flags.hideFlag(END_USER_FLAG_ID_SHARE_VARIANT);
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: `${FLAG_ID_BASE}DismissClick`,
							source,
						},
					}).fire();
				},
			};
			void flags.showFlag(sharePageDescriptor);
		}
	}, [flags, loading, onCopy, isInviteLinkCopied, source, createAnalyticsEvent, data]);

	if (error) {
		markErrorAsHandled(error);
	}

	return null;
};
